export interface SecretCollectionPin {
    salt: string;
    hash: string;
    algorithm?: 'PBKDF2';
    iterations?: number;
}

const PIN_PATTERN = /^\d{4,6}$/;
const PIN_HASH_ITERATIONS = 150_000;

const toHex = (bytes: Uint8Array): string =>
    Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');

const createSalt = (): string => {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return toHex(bytes);
};

const hashLegacyPin = async (pin: string, salt: string): Promise<string> => {
    const value = new TextEncoder().encode(`${salt}:${pin}`);
    const digest = await window.crypto.subtle.digest('SHA-256', value);
    return toHex(new Uint8Array(digest));
};

const hashPin = async (pin: string, salt: string, iterations: number): Promise<string> => {
    const pinKey = await window.crypto.subtle.importKey(
        'raw',
        new TextEncoder().encode(pin),
        'PBKDF2',
        false,
        ['deriveBits'],
    );
    const derivedBits = await window.crypto.subtle.deriveBits({
        name: 'PBKDF2',
        hash: 'SHA-256',
        salt: new TextEncoder().encode(salt),
        iterations,
    }, pinKey, 256);
    return toHex(new Uint8Array(derivedBits));
};

export const isValidSecretCollectionPin = (pin: string): boolean => PIN_PATTERN.test(pin);

export const createSecretCollectionPin = async (pin: string): Promise<SecretCollectionPin> => {
    const salt = createSalt();
    return {
        salt,
        hash: await hashPin(pin, salt, PIN_HASH_ITERATIONS),
        algorithm: 'PBKDF2',
        iterations: PIN_HASH_ITERATIONS,
    };
};

export const verifySecretCollectionPin = async (
    pin: string,
    secretPin: SecretCollectionPin,
): Promise<boolean> => {
    const hash = secretPin.algorithm === 'PBKDF2'
        ? await hashPin(pin, secretPin.salt, secretPin.iterations || PIN_HASH_ITERATIONS)
        : await hashLegacyPin(pin, secretPin.salt);
    return hash === secretPin.hash;
};
